import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import FeatureGate from "../components/FeatureGate";
import AppHeader from "../components/AppHeader";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Slider } from "../components/ui/slider";
import { Switch } from "../components/ui/switch";
import { Badge } from "../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { useAuth } from "../contexts/AuthContext";
import { useTheme } from "../contexts/ThemeContext";
import axios from "axios";
import { toast } from "sonner";

import {
  Save, ArrowLeft, Zap, FileText, Code, Search,
  Globe, BarChart3, Bot, Settings, Coins,
  Sparkles, X, MessageSquare, RotateCcw, Send
} from "lucide-react";

axios.defaults.headers.common["ngrok-skip-browser-warning"] = "true";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Icon string → React component map (stays in frontend, icons are React imports)
const iconComponents = { Bot, Search, Zap, FileText, Code, Globe, BarChart3, Settings };

const FALLBACK_METADATA = {
  categories: [
    // Core Business
    { id: "cat_finance", name: "Finance & Accounting", icon: "BarChart3" },
    { id: "cat_banking", name: "Banking & Financial Services", icon: "BarChart3" },
    { id: "cat_insurance", name: "Insurance", icon: "FileText" },
    { id: "cat_legal", name: "Legal & Compliance", icon: "FileText" },
    { id: "cat_consulting", name: "Consulting & Strategy", icon: "BarChart3" },
    { id: "cat_marketing", name: "Marketing & Sales", icon: "Globe" },
    { id: "cat_ecommerce", name: "E-Commerce & Retail", icon: "Globe" },
    { id: "cat_hr", name: "Human Resources", icon: "Search" },
    { id: "cat_support", name: "Customer Support", icon: "Bot" },
    { id: "cat_operations", name: "Operations & Logistics", icon: "Settings" },
    { id: "cat_supply_chain", name: "Supply Chain & Procurement", icon: "Settings" },
    { id: "cat_real_estate", name: "Real Estate", icon: "Globe" },
    // Science & Technology
    { id: "cat_engineering", name: "Software Engineering", icon: "Code" },
    { id: "cat_data_science", name: "Data Science & Analytics", icon: "BarChart3" },
    { id: "cat_cybersecurity", name: "Cybersecurity & Information Security", icon: "Code" },
    { id: "cat_iot", name: "IoT & Embedded Systems", icon: "Code" },
    { id: "cat_robotics", name: "Robotics & Automation", icon: "Bot" },
    { id: "cat_blockchain", name: "Blockchain & Web3", icon: "Code" },
    { id: "cat_gaming", name: "Gaming & Interactive Media", icon: "Code" },
    { id: "cat_telecom", name: "Telecommunications", icon: "Globe" },
    { id: "cat_aerospace", name: "Aerospace & Defense", icon: "Zap" },
    { id: "cat_research", name: "Research & Development", icon: "Search" },
    // Healthcare & Life Sciences
    { id: "cat_healthcare", name: "Healthcare & Medical", icon: "Zap" },
    { id: "cat_biotech", name: "Biotechnology & Pharmaceuticals", icon: "Zap" },
    // Industry & Resources
    { id: "cat_manufacturing", name: "Manufacturing & Industrial", icon: "Settings" },
    { id: "cat_construction", name: "Construction & Architecture", icon: "Settings" },
    { id: "cat_energy", name: "Energy & Utilities", icon: "Zap" },
    { id: "cat_mining", name: "Mining & Natural Resources", icon: "Globe" },
    { id: "cat_agriculture", name: "Agriculture & Food Science", icon: "Globe" },
    { id: "cat_automotive", name: "Automotive & Transportation", icon: "Settings" },
    { id: "cat_environment", name: "Environmental & Sustainability", icon: "Globe" },
    // Media, Education & Public
    { id: "cat_education", name: "Education & Training", icon: "FileText" },
    { id: "cat_entertainment", name: "Entertainment & Media", icon: "Globe" },
    { id: "cat_media", name: "Publishing & Content Creation", icon: "FileText" },
    { id: "cat_government", name: "Government & Public Sector", icon: "FileText" },
    { id: "cat_nonprofit", name: "Non-Profit & Social Impact", icon: "Globe" },
    // Lifestyle & Services
    { id: "cat_hospitality", name: "Hospitality & Tourism", icon: "Globe" },
    { id: "cat_travel", name: "Travel & Aviation", icon: "Globe" },
    { id: "cat_sports", name: "Sports & Fitness", icon: "Zap" },
    { id: "cat_fashion", name: "Fashion & Apparel", icon: "Globe" },
    // Catch-all
    { id: "cat_general", name: "General / Other", icon: "Bot" },
  ],
  tools: [
    { id: "tool_web_search", name: "Web Search", description: "Search the web for information" },
    { id: "tool_web_scrape", name: "Web Scraper", description: "Extract data from web pages" },
    //{ id: "tool_data_analysis", name: "Data Analyzer", description: "Analyze and visualize data" },
    { id: "tool_template_render", name: "Template Engine", description: "Fill templates with data" },
    { id: "tool_email_draft", name: "Email Drafter", description: "Draft professional emails" },
    //{ id: "tool_safe_code_exec", name: "Code Executor", description: "Execute code snippets safely" },
  ],
  triggers: [
    { id: "user_prompt", name: "User Prompt", description: "Triggered by user input" },
    // { id: "file_upload", name: "File Upload", description: "Triggered by file upload" },
  ],
  icons: ["Bot", "Search", "Zap", "FileText", "Code", "Globe", "BarChart3", "Settings"],
};

const AgentBuilder = () => {
  const { agentId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { token, isAuthenticated, user } = useAuth();
  const { isDark } = useTheme();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // ── Metadata from backend (single source of truth) ──
  const [categories, setCategories] = useState([]);
  const [tools, setTools] = useState([]);
  const [triggers, setTriggers] = useState([]);
  const [icons, setIcons] = useState([]);

  // AI Assistant state
  const [assistantOpen, setAssistantOpen] = useState(false);
  const [assistantMessages, setAssistantMessages] = useState([]);
  const [assistantInput, setAssistantInput] = useState("");
  const [assistantLoading, setAssistantLoading] = useState(false);
  const [showQuickPrompt, setShowQuickPrompt] = useState(true);
  const [quickPromptInput, setQuickPromptInput] = useState("");
  const chatEndRef = useRef(null);

  const [formData, setFormData] = useState({
    name: "",
    description: "",
    category_id: "",
    goals: "",
    triggers: ["user_prompt"],
    autonomy_level: 5,
    tools: [],
    icon: "Bot",
    is_public: false,
    pricing_type: "credits",
    credits_per_run: 2,
  });



  // Check feature access — skip API calls if user cannot access Agent Builder
  const hasBuilderAccess =
    user?.plan === 'enterprise_pro' ||
    (user?.feature_access || []).includes('agent_builder');

  useEffect(() => {
    if (hasBuilderAccess && agentId) {
      fetchAgent();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agentId, hasBuilderAccess]);

  // Per-tool credit costs — must mirror backend TOOL_CREDITS in tool_catalog.py
  const TOOL_CREDIT_COSTS = {
    tool_web_search:      1,
    tool_web_scrape:      1,
    tool_doc_gen:         3,
    tool_template_render: 1,
    tool_email_draft:     1,
  };

  // Compute total credits: 2 base + sum of per-tool costs
  const computeCredits = (selectedTools) =>
    2 + selectedTools.reduce((sum, tid) => sum + (TOOL_CREDIT_COSTS[tid] ?? 1), 0);

  // Fetch builder metadata from backend (single source of truth)
  useEffect(() => {
    if (!hasBuilderAccess) return;
    const fetchMetadata = async () => {
      try {
        const res = await axios.get(`${API}/agent-builder/metadata`);
        const { categories: cats, tools: tls, triggers: trgs, icons: ics } = res.data;

        // Only expose the 5 supported tools; block legacy/dev-only tools
        const allowedToolIds = ["tool_web_search", "tool_web_scrape", "tool_doc_gen", "tool_template_render", "tool_email_draft"];
        const blockedTriggerIds = ["file_upload"];

        setCategories(
          (cats || []).map((c) => ({
            ...c,
            icon: iconComponents[c.icon] || Bot,
          }))
        );
        // Merge backend metadata with local credit costs
        setTools(
          (tls || [])
            .filter(t => allowedToolIds.includes(t.id))
            .map(t => ({ ...t, credits: TOOL_CREDIT_COSTS[t.id] ?? 1 }))
        );
        setTriggers((trgs || []).filter(t => !blockedTriggerIds.includes(t.id)));
        setIcons(ics || []);
      } catch (err) {
        console.error("Failed to load builder metadata:", err);
        const { categories: cats, tools: tls, triggers: trgs, icons: ics } = FALLBACK_METADATA;
        setCategories(
          (cats || []).map((c) => ({
            ...c,
            icon: iconComponents[c.icon] || Bot,
          }))
        );
        setTools(tls || []);
        setTriggers(trgs || []);
        setIcons(ics || []);
        toast.error("Builder metadata request failed. Loaded fallback options.");
      }
    };
    fetchMetadata();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasBuilderAccess]);

  const fetchAgent = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/custom-agents/${agentId}`);
      setFormData({
        name: response.data.name || "",
        description: response.data.description || "",
        category_id: response.data.category_id || "",
        goals: response.data.goals || "",
        triggers: response.data.triggers || ["user_prompt"],
        autonomy_level: response.data.autonomy_level || 5,
        tools: response.data.tools || [],
        icon: response.data.icon || "Bot",
        is_public: response.data.is_public ?? true,
        pricing_type: "credits",
        // Recompute from stored tools so the display is always accurate
        credits_per_run: computeCredits(response.data.tools || []),
      });
    } catch (error) {
      console.error("Error fetching agent:", error);
      toast.error("Failed to load agent");
    }
    setLoading(false);
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();

    if (!formData.name || !formData.description || !formData.goals) {
      toast.error("Please fill in all required fields");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...formData,
        // Persist computed credits so the backend record is accurate
        credits_per_run: computeCredits(formData.tools),
      };
      if (agentId) {
        await axios.put(`${API}/custom-agents/${agentId}`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success("Agent updated successfully!");
        navigate(`/agent/${agentId}`);
      } else {
        const response = await axios.post(`${API}/custom-agents`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success("Agent created successfully!");
        navigate(`/agent/${response.data.agent_id}`);
      }
    } catch (error) {
      console.error("Error saving agent:", error);
      toast.error(error.response?.data?.detail || "Failed to save agent");
    }
    setSaving(false);
  };

  const toggleTool = (toolId) => {
    setFormData((prev) => ({
      ...prev,
      tools: prev.tools.includes(toolId)
        ? prev.tools.filter((t) => t !== toolId)
        : [...prev.tools, toolId],
    }));
  };

  const toggleTrigger = (triggerId) => {
    setFormData((prev) => ({
      ...prev,
      triggers: prev.triggers.includes(triggerId)
        ? prev.triggers.filter((t) => t !== triggerId)
        : [...prev.triggers, triggerId],
    }));
  };

  // Scroll assistant chat to bottom
  useEffect(() => {
    if (chatEndRef.current) {
      chatEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [assistantMessages, assistantLoading]);

  // Send message to AI assistant
  const sendToAssistant = async (message, reset = false) => {
    if (!message.trim()) return;
    if (!token) {
      toast.error("Please sign in to use the AI assistant");
      return;
    }

    const userMsg = { role: "user", content: message.trim() };
    setAssistantMessages((prev) => (reset ? [userMsg] : [...prev, userMsg]));
    setAssistantInput("");
    setAssistantLoading(true);
    setShowQuickPrompt(false);
    setAssistantOpen(true);

    try {
      const res = await axios.post(
        `${API}/agent-builder/assist`,
        {
          message: message.trim(),
          current_form: formData,
          reset,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const { reply, form_data } = res.data;

      // Apply form data if returned
      if (form_data) {
        setFormData((prev) => ({ ...prev, ...form_data }));
        toast.success("Fields updated by AI assistant");
      }

      setAssistantMessages((prev) => [
        ...prev,
        { role: "assistant", content: reply, hasFormData: !!form_data },
      ]);
    } catch (error) {
      console.error("Assistant error:", error);

      // ── Client-side fallback: generate form_data locally so agent creation never fails ──
      const words = message.trim().replace(/[^a-zA-Z0-9\s]/g, "").split(/\s+/);
      const fallbackName = words.slice(0, 6).map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");
      const msgLower = message.toLowerCase();
      let fallbackCat = "cat_general";
      for (const cat of categories) {
        const catWords = cat.name.toLowerCase().split(/[\s&/]+/).filter(w => w.length > 2);
        if (catWords.some(kw => msgLower.includes(kw))) {
          fallbackCat = cat.id;
          break;
        }
      }
      const fallbackData = {
        name: fallbackName.slice(0, 60),
        description: message.trim().slice(0, 300),
        category_id: fallbackCat,
        goals: `Primary goal: ${message.trim()}\n\n- Understand user requirements and provide expert assistance\n- Deliver clear, actionable, and accurate responses`,
        triggers: ["user_prompt"],
        autonomy_level: 5,
        tools: ["tool_web_search"].filter(tid => tools.some(t => t.id === tid)),
        icon: "Bot",
        is_public: false,
        pricing_type: "credits",
        credits_per_run: 2,
      };
      setFormData((prev) => ({ ...prev, ...fallbackData }));
      toast.success("AI assistant unavailable — fields auto-filled from your description");

      setAssistantMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: "I had trouble reaching the AI service, but I've auto-filled the form based on your description. Feel free to adjust any fields!",
          hasFormData: true,
        },
      ]);
    }
    setAssistantLoading(false);
  };

  // Handle quick prompt submission
  const handleQuickPrompt = (e) => {
    if (e) e.preventDefault();
    if (quickPromptInput.trim()) {
      sendToAssistant(quickPromptInput, true);
      setQuickPromptInput("");
    }
  };

  // Handle chat message submission
  const handleAssistantSend = (e) => {
    if (e) e.preventDefault();
    sendToAssistant(assistantInput);
  };

  // Reset assistant conversation
  const resetAssistant = () => {
    setAssistantMessages([]);
    setShowQuickPrompt(true);
    sendToAssistant("Start fresh — I want to build a new agent.", true);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <AppHeader />
        <div className="pt-24 flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#F87217]"></div>
        </div>
      </div>
    );
  }

  const headerRight = isAuthenticated ? (
    <div className="flex items-center gap-4">
      <div className="flex items-center gap-2 px-3 py-1.5 rounded-sm" style={{ background: 'rgba(255,255,255,0.06)' }}>
        <Coins className="w-4 h-4 text-[#F87217]" />
        <span className="text-sm font-medium text-slate-200">{user?.credits || 0}</span>
      </div>
      <Button onClick={() => navigate("/dashboard")} className="bg-[#F87217] hover:bg-[#C65B12] text-white rounded-sm">Dashboard</Button>
    </div>
  ) : (
    <Button onClick={() => navigate("/login")} className="bg-[#F87217] hover:bg-[#C65B12] text-white rounded-sm">Sign In</Button>
  );

  return (
    <div className="min-h-screen bg-slate-50">
      <AppHeader rightContent={headerRight} />

      <FeatureGate feature="agent_builder" featureLabel="Create">
        {/* Page Content */}
        <div className="max-w-5xl mx-auto px-6 py-10">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate(-1)}
                className="p-2 rounded-lg transition-colors duration-200"
                style={{ color: '#94a3b8' }}
                onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.1)'; e.currentTarget.style.color = '#f0f6fc'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#94a3b8'; }}
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-3xl font-bold text-slate-900 font-['Outfit']">
                  {agentId ? "Edit Agent" : "Create New Agent"}
                </h1>
                <p className="text-slate-500">
                  {agentId ? "Update your agent configuration" : "Define your AI agent with no code"}
                </p>
              </div>
            </div>
            <Button
              onClick={handleSubmit}
              disabled={saving}
              className="bg-[#F87217] hover:bg-[#C65B12] text-white rounded-sm"
            >
              {saving ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  {agentId ? "Save Changes" : "Create Agent"}
                </>
              )}
            </Button>
          </div>

          {/* AI Auto-Fill Quick Prompt (shown when assistant panel is closed and no conversation yet) */}
          {showQuickPrompt && !agentId && (
            <div
              className="p-6 rounded-sm mb-8"
              style={{
                background: isDark
                  ? 'linear-gradient(90deg, rgba(248,114,23,0.16), rgba(245,158,11,0.10))'
                  : 'linear-gradient(90deg, #fff7ed, #fffbeb)',
                border: `1px solid ${isDark ? 'rgba(248,114,23,0.35)' : '#fed7aa'}`,
              }}
            >
              <div className="flex items-start gap-4">
                <div
                  className="p-3 rounded-lg shadow-sm"
                  style={{
                    background: isDark ? 'rgba(15,23,41,0.9)' : '#fff',
                    border: `1px solid ${isDark ? 'rgba(248,114,23,0.3)' : '#fed7aa'}`,
                  }}
                >
                  <Sparkles className="w-6 h-6 text-[#F87217]" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold mb-1" style={{ color: isDark ? '#f0f6fc' : '#0f172a' }}>Auto-fill with AI</h3>
                  <p className="text-sm mb-4" style={{ color: isDark ? '#cbd5e1' : '#475569' }}>
                    Describe the agent you want to build in plain English and let AI fill in all the fields for you.
                  </p>
                  <form onSubmit={handleQuickPrompt} className="flex gap-3">
                    <Input
                      value={quickPromptInput}
                      onChange={(e) => setQuickPromptInput(e.target.value)}
                      placeholder='e.g. "A customer support agent that handles refund requests and escalates complex issues"'
                      className="h-11 flex-1"
                      style={{
                        background: isDark ? '#0f1729' : '#fff',
                        borderColor: isDark ? '#334155' : '#e2e8f0',
                        color: isDark ? '#e2e8f0' : '#0f172a',
                      }}
                    />
                    <Button
                      type="submit"
                      disabled={!quickPromptInput.trim() || assistantLoading}
                      className="bg-[#F87217] hover:bg-[#C65B12] text-white rounded-sm h-11 px-6"
                    >
                      {assistantLoading ? (
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                      ) : (
                        <>
                          <Sparkles className="w-4 h-4 mr-2" />
                          Generate
                        </>
                      )}
                    </Button>
                  </form>
                </div>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-8">
            {/* Basic Info */}
            <div className="p-6 rounded-sm bg-white border border-slate-200">
              <h2 className="text-xl font-semibold text-slate-900 mb-6 flex items-center gap-2">
                <Bot className="w-5 h-5 text-[#F87217]" />
                Basic Information
              </h2>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="name">Agent Name *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
                    placeholder="My Awesome Agent"
                    className="h-11"
                  />
                </div>

                {/* Category — Radix Select to fix Firefox dropdown bug */}
                <div className="space-y-2">
                  <Label htmlFor="category">Category</Label>
                  <Select
                    value={formData.category_id}
                    onValueChange={(value) => setFormData((prev) => ({ ...prev, category_id: value }))}
                  >
                    <SelectTrigger id="category" className="h-11 rounded-sm border-slate-200 bg-white text-sm text-slate-900">
                      <SelectValue placeholder="Select a category" />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map((cat) => (
                        <SelectItem key={cat.id} value={cat.id}>
                          {cat.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="md:col-span-2 space-y-2">
                  <Label htmlFor="description">Description *</Label>
                  <Textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData((prev) => ({ ...prev, description: e.target.value }))}
                    placeholder="Describe what your agent does..."
                    rows={3}
                  />
                </div>
              </div>
            </div>

            {/* Goals & Tasks */}
            <div className="p-6 rounded-sm bg-white border border-slate-200">
              <h2 className="text-xl font-semibold text-slate-900 mb-6 flex items-center gap-2">
                <Zap className="w-5 h-5 text-[#F87217]" />
                Goals & Tasks
              </h2>
              <div className="space-y-2">
                <Label htmlFor="goals">Agent Goals *</Label>
                <Textarea
                  id="goals"
                  value={formData.goals}
                  onChange={(e) => setFormData((prev) => ({ ...prev, goals: e.target.value }))}
                  placeholder="Describe what tasks the agent should accomplish. Be specific about the expected outcomes..."
                  rows={5}
                />
                <p className="text-sm text-slate-500">
                  Tip: Be specific about inputs, outputs, and success criteria
                </p>
              </div>
            </div>

            {/* Triggers */}
            <div className="p-6 rounded-sm bg-white border border-slate-200">
              <h2 className="text-xl font-semibold text-slate-900 mb-6 flex items-center gap-2">
                <Settings className="w-5 h-5 text-[#F87217]" />
                Triggers
              </h2>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {triggers.map((trigger) => (
                  <button
                    key={trigger.id}
                    type="button"
                    onClick={() => toggleTrigger(trigger.id)}
                    className={`p-4 rounded-sm border text-left transition-all duration-200 ${formData.triggers.includes(trigger.id)
                      ? "border-[#F87217] bg-orange-50"
                      : "border-slate-200 hover:border-[#F87217]/50"
                      }`}
                  >
                    <p className="font-medium text-slate-900">{trigger.name}</p>
                    <p className="text-sm text-slate-500">{trigger.description}</p>
                  </button>
                ))}
              </div>
            </div>

            {/* Tools */}
            <div className="p-6 rounded-sm bg-white border border-slate-200">
              <h2 className="text-xl font-semibold text-slate-900 mb-6 flex items-center gap-2">
                <Code className="w-5 h-5 text-[#F87217]" />
                Tool Integrations
              </h2>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {tools.map((tool) => {
                  const selected = formData.tools.includes(tool.id);
                  const toolCredits = tool.credits ?? TOOL_CREDIT_COSTS[tool.id] ?? 1;
                  return (
                    <button
                      key={tool.id}
                      type="button"
                      onClick={() => toggleTool(tool.id)}
                      className={`p-4 rounded-sm border text-left transition-all duration-200 relative ${selected
                        ? "border-[#F87217] bg-orange-50"
                        : "border-slate-200 hover:border-[#F87217]/50"
                      }`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <p className="font-medium text-slate-900">{tool.name}</p>
                        <span
                          className={`flex-shrink-0 text-xs font-semibold px-2 py-0.5 rounded-full ${
                            selected
                              ? "bg-[#F87217] text-white"
                              : "bg-slate-100 text-slate-500"
                          }`}
                        >
                          +{toolCredits} credits
                        </span>
                      </div>
                      <p className="text-sm text-slate-500 mt-1">{tool.description}</p>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Autonomy & Settings */}
            <div className="p-6 rounded-sm bg-white border border-slate-200">
              <h2 className="text-xl font-semibold text-slate-900 mb-6 flex items-center gap-2">
                <Bot className="w-5 h-5 text-[#F87217]" />
                Autonomy & Settings
              </h2>
              <div className="space-y-8">
                {/* Autonomy slider */}
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <Label>Autonomy Level</Label>
                    <span className="text-sm font-medium text-[#F87217]">{formData.autonomy_level}/10</span>
                  </div>
                  <Slider
                    value={[formData.autonomy_level]}
                    onValueChange={(value) => setFormData((prev) => ({ ...prev, autonomy_level: value[0] }))}
                    min={1}
                    max={10}
                    step={1}
                    className="w-full"
                  />
                  <p className="text-sm text-slate-500">
                    Higher autonomy means the agent makes more decisions independently
                  </p>
                </div>

                {/* Public toggle */}
                <div className="flex items-center justify-between">
                  <div>
                    <Label>Make Public</Label>
                    <p className="text-sm text-slate-500">Allow others to discover and use this agent</p>
                  </div>
                  <Switch
                    checked={formData.is_public}
                    onCheckedChange={(checked) => setFormData((prev) => ({ ...prev, is_public: checked }))}
                  />
                </div>

                {/* Pricing — credits only (no free option) */}
                <div className="space-y-2">
                  <Label>Estimated Total per Query</Label>
                  <div className="h-11 flex items-center px-3 rounded-sm border border-slate-200 bg-slate-50">
                    <Coins className="w-4 h-4 text-[#F87217] mr-2" />
                    <span className="text-sm font-semibold text-slate-900">
                      {computeCredits(formData.tools)} credits
                    </span>
                  </div>
                  <p className="text-xs text-slate-500">
                    2 base
                    {formData.tools.length > 0 && (
                      <> + {formData.tools.map(tid => {
                        const cost = TOOL_CREDIT_COSTS[tid] ?? 1;
                        const name = (tools.find(t => t.id === tid) || FALLBACK_METADATA.tools.find(t => t.id === tid))?.name || tid;
                        return `${name} (+${cost})`;
                      }).join(", ")}</>
                    )}
                  </p>
                </div>

                {/* Icon picker */}
                <div className="space-y-2">
                  <Label>Icon</Label>
                  <div className="flex gap-2 flex-wrap">
                    {icons.map((iconName) => {
                      const IconComp = iconComponents[iconName];
                      return (
                        <button
                          key={iconName}
                          type="button"
                          onClick={() => setFormData((prev) => ({ ...prev, icon: iconName }))}
                          className={`p-3 rounded-sm border transition-all duration-200 ${formData.icon === iconName
                            ? "border-[#F87217] bg-orange-50"
                            : "border-slate-200 hover:border-[#F87217]/50"
                            }`}
                        >
                          {IconComp && <IconComp className="w-5 h-5" />}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>

            {/* Publish Button at Bottom */}
            <div className="flex justify-end pt-6 border-t border-slate-200">
              <Button
                type="submit"
                disabled={saving}
                className="bg-[#F87217] hover:bg-[#C65B12] text-white rounded-sm px-8 py-3 text-base font-semibold"
              >
                {saving ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
                ) : (
                  <>
                    <Zap className="w-5 h-5 mr-2" />
                    {agentId ? "Update & Publish" : "Publish Agent"}
                  </>
                )}
              </Button>
            </div>
          </form>
        </div>

        {/* Floating AI Assistant Toggle Button */}
        {!assistantOpen && !showQuickPrompt && (
          <button
            onClick={() => setAssistantOpen(true)}
            className="fixed bottom-6 right-6 z-50 p-4 rounded-full bg-[#F87217] text-white shadow-lg hover:bg-[#C65B12] transition-all duration-200 hover:scale-105"
            title="Open AI Assistant"
          >
            <MessageSquare className="w-6 h-6" />
          </button>
        )}

        {/* AI Assistant Chat Panel */}
        {assistantOpen && (
          <div className="fixed bottom-6 right-6 z-50 w-[420px] max-h-[600px] bg-white rounded-lg shadow-2xl border border-slate-200 flex flex-col overflow-hidden">
            {/* Panel Header */}
            <div className="flex items-center justify-between px-4 py-3 bg-gradient-to-r from-[#F87217] to-[#C65B12] text-white">
              <div className="flex items-center gap-2">
                <Sparkles className="w-5 h-5" />
                <span className="font-semibold text-sm">AI Builder Assistant</span>
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={resetAssistant}
                  className="p-1.5 hover:bg-white/20 rounded transition-colors"
                  title="Reset conversation"
                >
                  <RotateCcw className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setAssistantOpen(false)}
                  className="p-1.5 hover:bg-white/20 rounded transition-colors"
                  title="Close"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3 min-h-[300px] max-h-[440px]">
              {assistantMessages.length === 0 && (
                <div className="text-center py-8">
                  <Sparkles className="w-10 h-10 text-orange-300 mx-auto mb-3" />
                  <p className="text-sm text-slate-500 font-medium">Ask me to help configure your agent</p>
                  <p className="text-xs text-slate-400 mt-1">e.g. "Make the autonomy level higher" or "Change it to a legal agent"</p>
                </div>
              )}
              {assistantMessages.map((msg, idx) => (
                <div
                  key={idx}
                  className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"
                    }`}
                >
                  <div
                    className={`max-w-[85%] px-3.5 py-2.5 rounded-lg text-sm leading-relaxed ${msg.role === "user"
                      ? "bg-[#F87217] text-white rounded-br-sm"
                      : "bg-slate-100 text-slate-800 rounded-bl-sm"
                      }`}
                  >
                    {msg.content}
                    {msg.hasFormData && (
                      <div className="mt-2 pt-2 border-t border-slate-200/50 flex items-center gap-1 text-xs opacity-80">
                        <Zap className="w-3 h-3" />
                        Fields updated in the form
                      </div>
                    )}
                  </div>
                </div>
              ))}
              {assistantLoading && (
                <div className="flex justify-start">
                  <div className="bg-slate-100 px-4 py-3 rounded-lg rounded-bl-sm">
                    <div className="flex gap-1.5">
                      <div className="w-2 h-2 rounded-full bg-slate-400 animate-bounce" style={{ animationDelay: "0ms" }} />
                      <div className="w-2 h-2 rounded-full bg-slate-400 animate-bounce" style={{ animationDelay: "150ms" }} />
                      <div className="w-2 h-2 rounded-full bg-slate-400 animate-bounce" style={{ animationDelay: "300ms" }} />
                    </div>
                  </div>
                </div>
              )}
              <div ref={chatEndRef} />
            </div>

            {/* Input */}
            <form
              onSubmit={handleAssistantSend}
              className="border-t border-slate-200 px-3 py-3 flex items-center gap-2 bg-slate-50"
            >
              <input
                value={assistantInput}
                onChange={(e) => setAssistantInput(e.target.value)}
                placeholder="Ask AI to change fields..."
                className="flex-1 text-sm px-3 py-2.5 rounded-md border border-slate-200 focus:outline-none focus:ring-2 focus:ring-[#F87217]/30 focus:border-[#F87217] bg-white"
                disabled={assistantLoading}
              />
              <Button
                type="submit"
                disabled={!assistantInput.trim() || assistantLoading}
                size="sm"
                className="bg-[#F87217] hover:bg-[#C65B12] text-white rounded-md h-10 w-10 p-0 flex items-center justify-center"
              >
                <Send className="w-4 h-4" />
              </Button>
            </form>
          </div>
        )}
      </FeatureGate>
    </div>
  );
};

export default AgentBuilder;

